import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { daysheetAPI, taskTimeAPI } from '../services/api';
import Layout from '../components/Layout';
import { getLocation } from '../utils/locationUtils';
import { toast } from 'sonner';
import './DailyWorkSummary.css';

const DailyWorkSummary = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        workSummary: '',
        blockers: '',
        commits: '',
        tomorrowPlan: '',
        workLocation: 'Office'
    });
    const [tasksFetched, setTasksFetched] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const user = JSON.parse(localStorage.getItem('user'));

    useEffect(() => {
        fetchTodayTasks();
        checkInitialStatus();

        // Navigation warning
        const handleBeforeUnload = (e) => {
            if (formData.workSummary.length > 0 && !submitting) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [formData.workSummary, submitting]);

    const checkInitialStatus = async () => {
        try {
            const res = await daysheetAPI.checkStatus();
            if (res.data.data.submitted) {
                toast.info('You have already submitted a daysheet for today.');
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Error checking daysheet status', err);
        }
    };

    const fetchTodayTasks = async () => {
        setLoading(true);
        try {
            const response = await taskTimeAPI.getMyLogs();
            const today = new Date().toISOString().split('T')[0];
            const todayTasks = response.data.data.filter(log =>
                new Date(log.startTime).toISOString().split('T')[0] === today
            );

            const taskMap = {};
            todayTasks.forEach(log => {
                if (!log.task) return;
                if (!taskMap[log.task._id]) {
                    taskMap[log.task._id] = {
                        title: log.task.title,
                        duration: 0
                    };
                }
                taskMap[log.task._id].duration += (log.duration || 0);
            });

            setTasksFetched(Object.values(taskMap));
        } catch (err) {
            console.warn('Failed to fetch today tasks for daysheet', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.workSummary.trim().length < 20) {
            setError('Your work summary is too short. Please provide at least 20 characters.');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            // 1. Submit Daysheet
            await daysheetAPI.submit(formData);

            // 2. Automatically Pulse Out
            const coords = await getLocation();
            await API.post('/attendance/punch-out', {
                workNotes: formData.workSummary,
                location: coords
            });

            toast.success('Daysheet submitted and Punched-Out successfully!');
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.message || 'Submission failed. Please try again.');
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (window.confirm('Are you sure? Punch-out will not be recorded until you submit your daysheet.')) {
            navigate('/dashboard');
        }
    };

    return (
        <Layout>
            <div className="summary-page-container">
                <div className="summary-content-card">
                    <header className="summary-card-header">
                        <div className="header-text">
                            <span className="date-chip">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                            <h1>Daily Work Summary</h1>
                            <p>Submission is mandatory before Punch-Out</p>
                        </div>
                    </header>

                    <form onSubmit={handleSubmit} className="summary-form">
                        {error && <div className="summary-error-box">{error}</div>}

                        <div className="summary-form-body">
                            <div className="info-puffy-grid">
                                <div className="info-item">
                                    <label>User</label>
                                    <span>{user?.name}</span>
                                </div>
                                <div className="info-item">
                                    <label>Role</label>
                                    <span>{user?.role}</span>
                                </div>
                            </div>

                            <div className="summary-form-group">
                                <label>Work Location <span className="req">*</span></label>
                                <div className="location-radio-group">
                                    <label className={`radio-pill ${formData.workLocation === 'Office' ? 'active' : ''}`}>
                                        <input type="radio" name="workLocation" value="Office" checked={formData.workLocation === 'Office'} onChange={handleChange} />
                                        Office
                                    </label>
                                    <label className={`radio-pill ${formData.workLocation === 'WFH' ? 'active' : ''}`}>
                                        <input type="radio" name="workLocation" value="WFH" checked={formData.workLocation === 'WFH'} onChange={handleChange} />
                                        WFH
                                    </label>
                                </div>
                            </div>

                            {tasksFetched.length > 0 && (
                                <div className="auto-tasks-section">
                                    <label>Tasks tracked today (Auto-fetched)</label>
                                    <div className="tasks-pills-wrap">
                                        {tasksFetched.map((t, i) => (
                                            <div key={i} className="task-mini-pill">
                                                <span className="t-name">{t.title}</span>
                                                <span className="t-dur">{(t.duration / 3600).toFixed(1)}h</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="summary-form-group">
                                <label>What was done today? <span className="req">*</span></label>
                                <textarea
                                    name="workSummary"
                                    value={formData.workSummary}
                                    onChange={handleChange}
                                    placeholder="Describe modules worked on, features delivered, or bugs fixed..."
                                    rows="5"
                                    required
                                />
                                <div className="form-meta-row">
                                    <span className="hint-text">Minimum 20 characters</span>
                                    <span className="count-text">{formData.workSummary.length} chars</span>
                                </div>
                            </div>

                            <div className="summary-form-row">
                                <div className="summary-form-group">
                                    <label>Blockers (Optional)</label>
                                    <textarea
                                        name="blockers"
                                        value={formData.blockers}
                                        onChange={handleChange}
                                        placeholder="Any technical hurdles?"
                                        rows="2"
                                    />
                                </div>
                                <div className="summary-form-group">
                                    <label>Commits / Links (Optional)</label>
                                    <input
                                        type="text"
                                        name="commits"
                                        value={formData.commits}
                                        onChange={handleChange}
                                        placeholder="Git links or PRs"
                                    />
                                </div>
                            </div>

                            <div className="summary-form-group">
                                <label>Tomorrow's Plan (Optional)</label>
                                <textarea
                                    name="tomorrowPlan"
                                    value={formData.tomorrowPlan}
                                    onChange={handleChange}
                                    placeholder="What's next on your plate?"
                                    rows="2"
                                />
                            </div>
                        </div>

                        <footer className="summary-card-footer">
                            <button type="button" onClick={handleCancel} className="btn-secondary" disabled={submitting}>Cancel</button>
                            <button type="submit" className="btn-primary" disabled={submitting}>
                                {submitting ? 'Processing Submission...' : 'Submit Daysheet & Punch-Out'}
                            </button>
                        </footer>
                    </form>
                </div>
            </div>
        </Layout>
    );
};

export default DailyWorkSummary;
